/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshButton from './ui/RefreshButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import {
  Coins, Package, Gift, Clock, Zap, Users, TrendingUp, TrendingDown,
  Plus, Edit, Trash2, Eye, Search, Filter, Download, RefreshCw,
  UserPlus, Cake, Award, ShoppingCart, FileText, Key, AlertTriangle,
  CheckCircle, XCircle, Calendar, Timer, Percent, Hash, ArrowUpRight,
  ArrowDownRight, BarChart3, PieChart, Activity, Settings, Star, CreditCard,
  HelpCircle, BookOpen
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import StripeCheckoutButton from './StripeCheckoutButton';
import Sparkline from './credits/Sparkline';
import CreditUsagePlansTab from './CreditUsagePlansTab';
import CreditStatsAdvancedTab from './CreditStatsAdvancedTab';
import CreditAlertsAdminTab from './CreditAlertsAdminTab';


export const GCUsersTab = ({ ctx }) => {
  const {
    actions, activeTab, attributeForm, attributeSearch, bonusForm, bonusRules,
    bulkAttributeForm, creditTypes, duplicateRaison, duplicateSource, duplicateTargetSearch,
    duplicateTargetUserId, editForm, editTarget, editingBonus, editingPack, loading,
    packForm, packs, searchTerm, selectedUserIds, showAttributeModal, showBonusModal,
    showBulkAttributeModal, showDuplicateModal, showEditModal, showPackModal, sparkSeries, stats,
    transactions, userBalances,
    setActions, setActiveTab, setAttributeForm, setAttributeSearch, setBonusForm, setBonusRules,
    setBulkAttributeForm, setCreditTypes, setDuplicateRaison, setDuplicateSource, setDuplicateTargetSearch,
    setDuplicateTargetUserId, setEditForm, setEditTarget, setEditingBonus, setEditingPack,
    setLoading, setPackForm, setPacks, setSearchTerm, setSelectedUserIds, setShowAttributeModal,
    setShowBonusModal, setShowBulkAttributeModal, setShowDuplicateModal, setShowEditModal, setShowPackModal,
    setSparkSeries, setStats, setTransactions, setUserBalances,
    exportUsersCsv, filteredUsers, handleAttributeCredits, handleBulkAttribute, handleBulkDelete,
    handleConfirmDuplicate, handleDeletePack, handleDeleteUserCredits, handleExportFiltered,
    handleExportSelection, handleSaveBonus, handleSaveEdit, handleSavePack, loadData, openDuplicateModal,
    openEditModal, renderIcon, resetPackForm, toggleSelectAllFiltered, toggleUserSelection,
    getAuthHeader, CountdownWidget, StatsCard, COLORS, AVAILABLE_ICONS,
  } = ctx;
  return (
        <TabsContent value="users" className="space-y-4">
          {/* Header carte avec titre, statistiques et recherche */}
          <Card className="border-2 border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">Attribution par utilisateur</h3>
                    <p className="text-sm text-slate-500">
                      {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} affiché{filteredUsers.length > 1 ? 's' : ''}
                      {searchTerm && ` · filtre "${searchTerm}"`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                  <div className="relative flex-1 sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                    <Input
                      placeholder="Rechercher nom, prénom ou email…"
                      className="pl-10 h-10 border-slate-300 focus-visible:ring-blue-500"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="credits-users-search"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                        aria-label="Effacer la recherche"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleExportFiltered}
                    className="h-10 border-slate-300 text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                    title={searchTerm ? `Exporter les ${filteredUsers.length} résultats filtrés` : 'Exporter tous les utilisateurs affichés'}
                    data-testid="credits-export-filtered-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Exporter CSV
                  </Button>
                  <Button
                    onClick={() => setShowAttributeModal(true)}
                    className="h-10 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md whitespace-nowrap"
                    data-testid="credits-attribuer-btn"
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    Attribuer des crédits
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Toolbar actions en masse — sticky avec gradient */}
          {selectedUserIds.size > 0 && (
            <div
              className="sticky top-2 z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white border border-blue-700 rounded-xl shadow-lg"
              data-testid="credits-bulk-toolbar"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/20 backdrop-blur">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-white">{selectedUserIds.size} utilisateur{selectedUserIds.size > 1 ? 's' : ''} sélectionné{selectedUserIds.size > 1 ? 's' : ''}</div>
                  <div className="text-xs text-blue-100">Choisissez une action à appliquer en masse</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleExportSelection}
                  className="bg-white text-slate-700 hover:bg-slate-100 shadow font-semibold"
                  data-testid="credits-bulk-export-btn"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Exporter sélection
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowBulkAttributeModal(true)}
                  className="bg-white text-blue-700 hover:bg-blue-50 shadow font-semibold"
                  data-testid="credits-bulk-attribute-btn"
                >
                  <Gift className="w-4 h-4 mr-1" />
                  Attribuer en masse
                </Button>
                <Button
                  size="sm"
                  onClick={handleBulkDelete}
                  className="bg-red-500 hover:bg-red-600 text-white shadow font-semibold"
                  data-testid="credits-bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Supprimer en masse
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedUserIds(new Set())}
                  className="text-white hover:bg-white/15"
                  data-testid="credits-bulk-clear-btn"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {/* Table utilisateurs */}
          <Card className="border border-slate-200 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                    <tr>
                      <th className="px-4 py-3.5 text-center w-12">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={filteredUsers.length > 0 && filteredUsers.every((u) => selectedUserIds.has(u.user_id))}
                          onChange={toggleSelectAllFiltered}
                          data-testid="credits-select-all"
                          aria-label="Sélectionner tous"
                        />
                      </th>
                      <th className="px-4 py-3.5 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Utilisateur</th>
                      <th className="px-3 py-3.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">
                        <div className="flex items-center justify-center gap-1.5">
                          <Coins className="w-4 h-4 text-blue-500" />
                          Standard
                        </div>
                      </th>
                      <th className="px-3 py-3.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">
                        <div className="flex items-center justify-center gap-1.5">
                          <Zap className="w-4 h-4 text-amber-500" />
                          Priorité
                        </div>
                      </th>
                      <th className="px-3 py-3.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">
                        <div className="flex items-center justify-center gap-1.5">
                          <Gift className="w-4 h-4 text-green-500" />
                          Bonus
                        </div>
                      </th>
                      <th className="px-3 py-3.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">
                        <div className="flex items-center justify-center gap-1.5">
                          <Clock className="w-4 h-4 text-red-500" />
                          Temporaire
                        </div>
                      </th>
                      <th className="px-3 py-3.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center py-16">
                          <div className="flex flex-col items-center gap-3">
                            <div className="p-4 rounded-full bg-slate-100">
                              <Users className="w-8 h-8 text-slate-400" />
                            </div>
                            <div>
                              <div className="font-semibold text-slate-700">Aucun utilisateur</div>
                              <div className="text-sm text-slate-500 mt-1">
                                {searchTerm ? `Aucun résultat pour "${searchTerm}"` : 'Aucun utilisateur avec un solde de crédits'}
                              </div>
                            </div>
                            {searchTerm && (
                              <Button variant="outline" size="sm" onClick={() => setSearchTerm('')}>
                                Effacer la recherche
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    {filteredUsers.map((user) => {
                      const isSelected = selectedUserIds.has(user.user_id);
                      const initials = `${(user.user_info?.prenom || '').charAt(0)}${(user.user_info?.nom || '').charAt(0)}`.toUpperCase() || '?';
                      const role = user.user_info?.role || '';
                      const roleColor = {
                        super_admin: 'bg-red-100 text-red-700 border-red-200',
                        admin: 'bg-purple-100 text-purple-700 border-purple-200',
                        cooper: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                        expert: 'bg-indigo-100 text-indigo-700 border-indigo-200',
                        member: 'bg-blue-100 text-blue-700 border-blue-200',
                      }[role.toLowerCase()] || 'bg-slate-100 text-slate-700 border-slate-200';

                      return (
                        <tr
                          key={user.id}
                          className={`transition-colors ${isSelected ? 'bg-blue-50/60' : 'hover:bg-slate-50'}`}
                          data-testid={`credits-user-row-${user.user_id}`}
                        >
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              checked={isSelected}
                              onChange={() => toggleUserSelection(user.user_id)}
                              data-testid={`credits-select-${user.user_id}`}
                              aria-label="Sélectionner cet utilisateur"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-slate-800 truncate flex items-center gap-2">
                                  {user.user_info?.is_test && (
                                    <span title="Compte de test" className="text-xs">🧪</span>
                                  )}
                                  {user.user_info?.prenom} {user.user_info?.nom}
                                  {role && (
                                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${roleColor} font-medium uppercase`}>
                                      {role}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500 truncate">{user.user_info?.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className={`inline-flex items-center justify-center min-w-[3rem] h-7 rounded-full px-2.5 font-semibold text-xs ${user.credits_standard > 0 ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
                              {user.credits_standard || 0}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className={`inline-flex items-center justify-center min-w-[3rem] h-7 rounded-full px-2.5 font-semibold text-xs ${user.credits_prioritaires > 0 ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
                              {user.credits_prioritaires || 0}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className={`inline-flex items-center justify-center min-w-[3rem] h-7 rounded-full px-2.5 font-semibold text-xs ${user.credits_bonus > 0 ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
                              {user.credits_bonus || 0}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <div className={`inline-flex items-center justify-center min-w-[3rem] h-7 rounded-full px-2.5 font-semibold text-xs ${user.credits_temporaires > 0 ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
                                {user.credits_temporaires || 0}
                              </div>
                              {user.expiration_temporaires && user.credits_temporaires > 0 && (
                                <CountdownWidget
                                  expirationDate={user.expiration_temporaires}
                                  credits={user.credits_temporaires}
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className={`inline-flex items-center justify-center min-w-[3.5rem] h-8 rounded-lg px-3 font-bold text-sm ${user.total_disponible > 0 ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow-sm' : 'bg-slate-100 text-slate-500'}`}>
                              {(user.total_disponible || 0).toLocaleString('fr-FR')}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm w-fit mx-auto">
                              <button
                                type="button"
                                title="Attribuer des crédits"
                                onClick={() => {
                                  setAttributeForm({ ...attributeForm, user_id: user.user_id });
                                  setShowAttributeModal(true);
                                }}
                                className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                                data-testid={`credits-action-attribute-${user.user_id}`}
                              >
                                <Gift className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                title="Modifier les crédits"
                                onClick={() => openEditModal(user)}
                                className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50 transition-colors"
                                data-testid={`credits-action-edit-${user.user_id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                title="Dupliquer vers un autre utilisateur"
                                onClick={() => openDuplicateModal(user)}
                                className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors"
                                data-testid={`credits-action-duplicate-${user.user_id}`}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                title="Supprimer (RAZ) les crédits"
                                onClick={() => handleDeleteUserCredits(user)}
                                className="p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                                data-testid={`credits-action-delete-${user.user_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length > 0 && (
                <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
                  <div>
                    Total : <span className="font-semibold text-slate-800">{filteredUsers.length}</span> utilisateur{filteredUsers.length > 1 ? 's' : ''}
                    {selectedUserIds.size > 0 && (
                      <span className="ml-3 text-blue-700">· {selectedUserIds.size} sélectionné{selectedUserIds.size > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span>Crédits cumulés affichés :</span>
                    <span className="font-bold text-slate-900">
                      {filteredUsers.reduce((acc, u) => acc + (u.total_disponible || 0), 0).toLocaleString('fr-FR')}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
  );
};

export default GCUsersTab;
